// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  Grid,
  Header,
  Icon,
  Link,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Tabs,
  TextContent,
  Toggle,
} from '@awsui/components-react';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { TranslateClient, TranslateTextCommand } from '@aws-sdk/client-translate';
import { API, Logger } from 'aws-amplify';
import { StandardRetryStrategy } from '@aws-sdk/middleware-retry';
import { getEmailFormattedSummary, getMarkdownSummary, getTextFileFormattedMeetingDetails } from '../common/summary';
import { COMPREHEND_PII_TYPES, DEFAULT_OTHER_SPEAKER_NAME, LANGUAGE_CODES } from '../common/constants';

import RecordingPlayer from '../recording-player';
import useSettingsContext from '../../contexts/settings';

import { DONE_STATUS, IN_PROGRESS_STATUS } from '../common/get-recording-status';
import { InfoLink } from '../common/info-link';
import { getWeightedSentimentLabel } from '../common/sentiment';

import { VoiceToneFluctuationChart, SentimentFluctuationChart, SentimentPerQuarterChart } from './sentiment-charts';

import './CallPanel.css';
import { SentimentTrendIcon } from '../sentiment-trend-icon/SentimentTrendIcon';
import { SentimentIcon } from '../sentiment-icon/SentimentIcon';
import useAppContext from '../../contexts/app';
import awsExports from '../../aws-exports';
import { downloadTranscriptAsExcel, downloadTranscriptAsText, exportToTextFile } from '../common/download-func';
import useCallsContext from '../../contexts/calls';
import { shareModal, deleteModal } from '../common/meeting-controls';
import updateTranscriptSegmentWithAnnotation from '../../graphql/queries/updateTranscriptSegmentWithAnnotation';
import updateTranscriptSegment from '../../graphql/queries/updateTranscriptSegment';
import addAnnotation from '../../graphql/queries/addAnnotation';

const logger = new Logger('CallPanel');

// comprehend PII types
const piiTypesSplitRegEx = new RegExp(`\\[(${COMPREHEND_PII_TYPES.join('|')})\\]`);

const MAXIMUM_ATTEMPTS = 100;
const ONE_HUNDRED_MS = 100;
const ONE_THOUSAND_MS = 1000;
const ANNOTATOR = 'Annotator';
// When trying to edit merged segments the whole aggregated segments is treated as the original segment
// Example: Segment 1: ABC, Segment 2: DEF after merge -> Segment 1: ABCDEF
// In the example above Segment 2 identifier is lost and only Segment 1 is getting edited
// If Segment 1 is edited then in the database and S3 the segments will be saved as Segment 1: ABCDEF, Segment 2: DEF
const PAUSE_TO_MERGE_IN_SECONDS = 0;
const MEETING_RECORD_EXPIRATION_IN_DAYS = 730;

/* eslint-disable react/prop-types, react/destructuring-assignment */
const CallAttributes = ({ item, setToolsOpen, getCallDetailsFromCallIds }) => {
  const { calls } = useCallsContext();
  const props = {
    calls,
    selectedItems: [item],
    loading: false,
    getCallDetailsFromCallIds,
  };

  return (
    <Container
      header={
        <Header
          variant="h4"
          info={<InfoLink onFollow={() => setToolsOpen(true)} />}
          actions={
            <SpaceBetween size="xxxs" direction="horizontal">
              {shareModal(props)} {deleteModal(props)}
            </SpaceBetween>
          }
        >
          Meeting Attributes
        </Header>
      }
    >
      <ColumnLayout columns={6} variant="text-grid">
        <SpaceBetween size="xs">
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              <strong>Meeting ID</strong>
            </Box>
            <div>{item.callId}</div>
          </div>
        </SpaceBetween>

        <SpaceBetween size="xs">
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              <strong>Initiation Timestamp</strong>
            </Box>
            <div>{item.initiationTimeStamp}</div>
          </div>
        </SpaceBetween>

        <SpaceBetween size="xs">
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              <strong>Last Update Timestamp</strong>
            </Box>
            <div>{item.updatedAt}</div>
          </div>
        </SpaceBetween>

        <SpaceBetween size="xs">
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              <strong>Duration</strong>
            </Box>
            <div>{item.conversationDurationTimeStamp}</div>
          </div>
        </SpaceBetween>

        <SpaceBetween size="xs">
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              <strong>Status</strong>
            </Box>
            <StatusIndicator type={item.recordingStatusIcon}>{` ${item.recordingStatusLabel} `}</StatusIndicator>
          </div>
        </SpaceBetween>
        {item?.pcaUrl?.length && (
          <SpaceBetween size="xs">
            <div>
              <Box margin={{ bottom: 'xxxs' }} color="text-label">
                <strong>Post Meeting Analytics</strong>
              </Box>
              <Button variant="normal" href={item.pcaUrl} target="_blank" iconAlign="right" iconName="external">
                Open in Post Call Analytics
              </Button>
            </div>
          </SpaceBetween>
        )}
        {item?.recordingUrl?.length && item?.recordingStatusLabel !== IN_PROGRESS_STATUS && (
          <SpaceBetween size="xs">
            <div>
              <Box margin={{ bottom: 'xxxs' }} color="text-label">
                <strong>Recording Audio</strong>
              </Box>
              <RecordingPlayer callId={item.callId} />
            </div>
          </SpaceBetween>
        )}
      </ColumnLayout>
    </Container>
  );
};

// eslint-disable-next-line arrow-body-style
const CallSummary = ({ item }) => {
  const downloadCallSummary = async (option) => {
    if (option.detail.id === 'download') {
      await exportToTextFile(getTextFileFormattedMeetingDetails(item), `Summary-${item.callId}`);
    } else if (option.detail.id === 'email') {
      window.open(`mailto:?subject=${item.callId}&body=${getEmailFormattedSummary(item.callSummaryText)}`);
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h4"
          info={
            <Link
              variant="info"
              target="_blank"
              href="https://docs.aws.amazon.com/transcribe/latest/dg/call-analytics-insights.html#call-analytics-insights-summarization"
            >
              Info
            </Link>
          }
          actions={
            <SpaceBetween size="xxs" direction="horizontal">
              {item.callSummaryText && (
                <ButtonDropdown
                  items={[
                    { text: 'Download summary', id: 'download', disabled: false, iconName: 'download' },
                    { text: 'Email summary (beta)', id: 'email', disabled: false, iconName: 'envelope' },
                  ]}
                  variant="normal"
                  onItemClick={(option) => downloadCallSummary(option)}
                >
                  <Icon name="download" variant="primary" />
                </ButtonDropdown>
              )}
            </SpaceBetween>
          }
        >
          Meeting Summary
        </Header>
      }
    >
      <Grid gridDefinition={[{ colspan: { default: 12 } }]}>
        <Tabs
          tabs={[
            {
              label: 'Transcript Summary',
              id: 'summary',
              content: (
                <div>
                  <div>
                    {/* eslint-disable-next-line react/no-array-index-key */}
                    <TextContent color="gray">
                      <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                        {getMarkdownSummary(item.callSummaryText)}
                      </ReactMarkdown>
                    </TextContent>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Grid>
    </Container>
  );
};

const getSentimentImage = (segment, enableSentimentAnalysis) => {
  const { sentiment, sentimentScore, sentimentWeighted } = segment;
  if (!sentiment || !enableSentimentAnalysis) {
    // returns an empty div to maintain spacing
    return <div className="sentiment-image" />;
  }
  const weightedSentimentLabel = getWeightedSentimentLabel(sentimentWeighted);
  return (
    <Popover
      dismissAriaLabel="Close"
      header="Sentiment"
      size="medium"
      triggerType="custom"
      content={
        <SpaceBetween size="s">
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              Sentiment
            </Box>
            <div>{sentiment}</div>
          </div>
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              Sentiment Scores
            </Box>
            <div>{JSON.stringify(sentimentScore)}</div>
          </div>
          <div>
            <Box margin={{ bottom: 'xxxs' }} color="text-label">
              Weighted Sentiment
            </Box>
            <div>{sentimentWeighted}</div>
          </div>
        </SpaceBetween>
      }
    >
      <div className="sentiment-image-popover">
        <SentimentIcon sentiment={weightedSentimentLabel} />
      </div>
    </Popover>
  );
};

const getTimestampFromSeconds = (secs) => {
  if (!secs || Number.isNaN(secs)) {
    return '00:00.0';
  }
  return new Date(secs * ONE_THOUSAND_MS).toISOString().substr(14, 7);
};

const TranscriptContent = ({ segment, translateCache, handleTranscriptSegmentUpdate }) => {
  const { settings } = useSettingsContext();
  const regex = settings?.CategoryAlertRegex ?? '.*';

  const { transcript, segmentId, channel, targetLanguage, translateOn } = segment;

  const segmentLanguageId = segmentId.concat('-', targetLanguage);

  // prettier-ignore
  const currTranslated = translateOn
    && targetLanguage !== ''
    && translateCache[segmentLanguageId] !== undefined
    && translateCache[segmentLanguageId].translated !== undefined
    ? translateCache[segmentLanguageId].translated
    : '';

  const result = currTranslated !== undefined ? currTranslated : '';

  const transcriptPiiSplit = transcript.split(piiTypesSplitRegEx);

  const transcriptComponents = transcriptPiiSplit.map((t, i) => {
    if (COMPREHEND_PII_TYPES.includes(t)) {
      // eslint-disable-next-line react/no-array-index-key
      return <Badge key={`${segmentId}-pii-${i}`} color="red">{`${t}`}</Badge>;
    }

    let className = '';
    let text = t;
    let translatedText = result;

    switch (channel) {
      case 'AGENT_ASSISTANT':
      case 'MEETING_ASSISTANT':
        className = 'transcript-segment-agent-assist';
        break;
      case 'AGENT':
      case 'CALLER':
        text = text.substring(text.indexOf(':') + 1).trim();
        translatedText = translatedText.substring(translatedText.indexOf(':') + 1).trim();
        break;
      case 'CATEGORY_MATCH':
        if (text.match(regex)) {
          className = 'transcript-segment-category-match-alert';
          text = `Alert: ${text}`;
        } else {
          className = 'transcript-segment-category-match';
          text = `Category: ${text}`;
        }
        break;
      default:
        break;
    }

    const [isEditingTranscript, setIsEditing] = useState(false);
    const [isEditingAnnotation, setEditingAnnotation] = useState(false);

    return (
      // prettier-ignore
      // eslint-disable-next-line react/no-array-index-key
      <TextContent key={`${segmentId}-text-${i}`} color="red" className={className}>
        {segment.edited && <span className="edited-transcript">(Edited)</span>}
        {!isEditingTranscript && <ReactMarkdown rehypePlugins={[rehypeRaw]}>{text.trim()}</ReactMarkdown>}
        <ReactMarkdown className="translated-text" rehypePlugins={[rehypeRaw]}>{translatedText.trim()}</ReactMarkdown>
        <div className="editing-buttons-section">
          {(segment.speaker !== ANNOTATOR && !isEditingAnnotation) && <EditTranscriptSegment
            segment={segment}
            setIsEditing={setIsEditing}
            handleUpdate={handleTranscriptSegmentUpdate}
          />}
          {!isEditingTranscript && <CommentButton
            segment={segment}
            handleUpdate={handleTranscriptSegmentUpdate}
            setIsEditing={setEditingAnnotation}
          />}
        </div>
      </TextContent>
    );
  });

  return (
    <div>
      <SpaceBetween>{transcriptComponents}</SpaceBetween>
    </div>
  );
};

const TranscriptSegment = ({
  segment,
  translateCache,
  enableSentimentAnalysis,
  showAnnotationsOnly,
  handleTranscriptSegmentUpdate,
}) => {
  const { channel } = segment;

  if (channel === 'CATEGORY_MATCH') {
    const categoryText = `${segment.transcript}`;
    const newSegment = segment;
    newSegment.transcript = categoryText;
    // We will return a special version of the grid that's specifically only for category.
    return (
      <Grid className="transcript-segment" disableGutters gridDefinition={[{ colspan: 1 }, { colspan: 10 }]}>
        {getSentimentImage(segment, enableSentimentAnalysis)}
        <SpaceBetween direction="vertical" size="xxs">
          <TranscriptContent segment={newSegment} translateCache={translateCache} />
        </SpaceBetween>
      </Grid>
    );
  }

  let displayChannel = `${segment.channel}`;
  let channelClass = '';

  if (channel === 'AGENT' || channel === 'CALLER') {
    displayChannel = `${segment.speaker}`.trim();
  } else if (channel === 'AGENT_ASSISTANT' || channel === 'MEETING_ASSISTANT') {
    displayChannel = 'MEETING_ASSISTANT';
    channelClass = 'transcript-segment-agent-assist';
  }

  return (
    <Grid className="transcript-segment" disableGutters gridDefinition={[{ colspan: 1 }, { colspan: 10 }]}>
      {getSentimentImage(segment, enableSentimentAnalysis)}
      <SpaceBetween direction="vertical" size="xxxs" className={channelClass}>
        <SpaceBetween direction="horizontal" size="xs">
          <TextContent>
            <strong>{displayChannel}</strong>
          </TextContent>
          <TextContent>
            {`${getTimestampFromSeconds(segment.startTime)} -
              ${getTimestampFromSeconds(segment.endTime)}`}
          </TextContent>
          <TextContent>
            <span className="annotation">{segment.annotation}</span>
          </TextContent>
        </SpaceBetween>
        {showAnnotationsOnly ? null : (
          <TranscriptContent
            segment={segment}
            translateCache={translateCache}
            handleTranscriptSegmentUpdate={handleTranscriptSegmentUpdate}
          />
        )}
      </SpaceBetween>
    </Grid>
  );
};

const EditTranscriptSegment = ({ segment, setIsEditing, handleUpdate }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startingInputText = segment.transcript || '';
  const [inputText, setInputText] = useState(startingInputText);
  const [submittedText, setSubmittedText] = useState(startingInputText);

  const handleEditClick = () => {
    setIsSubmitting(true);
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const result = await API.graphql({
      query: updateTranscriptSegment,
      variables: {
        input: {
          CallId: segment.callId,
          SegmentId: segment.segmentId,
          Transcript: inputText,
        },
      },
    });
    const submittedTranscript = result.data.updateTranscriptSegment.Transcript;
    const updatedTranscriptSegment = segment;
    setInputText(submittedTranscript);
    setSubmittedText(inputText);
    setIsSubmitting(false);
    updatedTranscriptSegment.transcript = submittedTranscript;
    updatedTranscriptSegment.edited = true;
    updatedTranscriptSegment.refreshTranslation = true;
    updatedTranscriptSegment.translated = false;
    setIsEditing(false);
    handleUpdate(updatedTranscriptSegment);
  };

  const handleCancel = async () => {
    setIsSubmitting(false);
    setIsEditing(false);
    setInputText(submittedText);
  };

  return (
    <div className="editor">
      {isSubmitting ? (
        <SpaceBetween direction="vertical" size="xxxxs">
          <textarea className="edit-transcript-box" value={inputText} onChange={(e) => setInputText(e.target.value)} />
          <div className="edit-transcript-buttons">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleSubmit} disabled={inputText === submittedText || inputText.trim() === ''}>
                Submit
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </SpaceBetween>
          </div>
        </SpaceBetween>
      ) : (
        <SpaceBetween>
          <div className="edit-transcript-buttons">
            <Button onClick={handleEditClick}>Edit Transcript</Button>
          </div>
        </SpaceBetween>
      )}
    </div>
  );
};

const CommentButton = ({ segment, handleUpdate, setIsEditing }) => {
  const [showTextInput, setShowTextInput] = useState(false);
  // isSubmitting to disable the buttons for a second the comment is being submitted.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startingInputText = segment.annotation || '';
  const [inputText, setInputText] = useState(startingInputText);
  // submittedText used for disabling submitting the same comment
  const [submittedText, setSubmittedText] = useState(startingInputText);
  const [isError, setIsError] = useState(false);

  const handleButtonClick = () => {
    setShowTextInput(true);
    setIsError(false);
    setIsEditing(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const result = await API.graphql({
      query: updateTranscriptSegmentWithAnnotation,
      variables: {
        input: {
          CallId: segment.callId,
          SegmentId: segment.segmentId,
          Annotation: inputText,
        },
      },
    });
    if (result.errors) {
      logger.error('Error occurred updating the transcript segment: ', result.errors[0]);
      setIsSubmitting(false);
      setShowTextInput(false);
      setIsError(true);
    } else {
      const submittedAnnotation = result.data.updateTranscriptSegmentWithAnnotation.Annotation;
      setShowTextInput(false);
      setInputText(submittedAnnotation);
      setSubmittedText(submittedAnnotation);
      const updatedSegment = segment;
      updatedSegment.annotation = submittedAnnotation;
      handleUpdate(updatedSegment);
      setTimeout(() => {
        setIsSubmitting(false);
      }, ONE_THOUSAND_MS);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setShowTextInput(false);
    setIsEditing(false);
    setInputText(submittedText);
  };

  return (
    <div className="editor">
      {!showTextInput ? (
        <Button onClick={handleButtonClick} disabled={isSubmitting}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isSubmitting ? 'Submitted' : isError ? 'Error - Try Again' : inputText ? 'Edit Comment' : 'Add Comment'}
        </Button>
      ) : (
        <SpaceBetween direction="vertical" size="xs">
          <textarea
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Enter your comment"
            style={{ width: '100%', height: '100px' }}
          />
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleSubmit} disabled={isSubmitting || inputText === submittedText}>
              Submit
            </Button>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      )}
    </div>
  );
};

/**
 * Check whether the current segment should be merged to the previous segment to get better
 * user experience. The conditions for merge are:
 * - Same speaker
 * - Same channel
 * - The gap between two segments is less than PAUSE_TO_MERGE_IN_SECONDS second
 * - The segment is from the recording of the meeting and not a pure annotation segment
 * - Add language code check if available
 * TODO: Check language code once it is returned
 * @param previous previous segment
 * @param current current segment
 * @returns {boolean} indicates whether to merge or not
 */
const shouldAppendToPreviousSegment = ({ previous, current }) =>
  // prettier-ignore
  // eslint-disable-next-line implicit-arrow-linebreak
  previous.speaker === current.speaker
  && previous.channel === current.channel
  && current.startTime - previous.endTime < PAUSE_TO_MERGE_IN_SECONDS
  && previous.speaker !== ANNOTATOR;

/**
 * Append current segment to its previous segment
 * @param previous previous segment
 * @param current current segment
 */
const appendToPreviousSegment = ({ previous, current }) => {
  /* eslint-disable no-param-reassign */
  previous.transcript += ` ${current.transcript}`;
  previous.endTime = current.endTime;
  previous.isPartial = current.isPartial;
};

const CallInProgressTranscript = ({
  item,
  callTranscriptPerCallId,
  autoScroll,
  translateClient,
  targetLanguage,
  agentTranscript,
  translateOn,
  collapseSentiment,
  enableSentimentAnalysis,
  showAnnotationsOnly,
}) => {
  const bottomRef = useRef();
  const [turnByTurnSegments, setTurnByTurnSegments] = useState([]);
  const [translateCache, setTranslateCache] = useState({});
  const [cacheSeen, setCacheSeen] = useState({});
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [updateFlag, setUpdateFlag] = useState(false);
  const [updatedTurnByTurnSegments, setUpdatedTurnByTurnSegments] = useState([]);
  const [editedSegment, setEditedSegment] = useState({});

  // channels: AGENT, AGENT_ASSIST, CALLER, CATEGORY_MATCH,
  // AGENT_VOICETONE, CALLER_VOICETONE
  const maxChannels = 6;
  const { callId } = item;
  const transcriptsForThisCallId = callTranscriptPerCallId[callId] || {};
  const transcriptChannels = Object.keys(transcriptsForThisCallId).slice(0, maxChannels);

  const getSegments = () => {
    const currentTurnByTurnSegments = transcriptChannels
      .map((c) => {
        const { segments } = transcriptsForThisCallId[c];
        return segments;
      })
      // sort entries by end time
      .reduce((p, c) => [...p, ...c].sort((a, b) => a.endTime - b.endTime), [])
      .map((c) => {
        const t = c;
        return t;
      });

    return currentTurnByTurnSegments;
  };

  const updateTranslateCache = (segments) => {
    const promises = [];
    // prettier-ignore
    for (let i = 0; i < segments.length; i += 1) {
      const segmentLanguageId = segments[i].segmentId.concat('-', targetLanguage);

      // prettier-ignore
      if (translateCache[segmentLanguageId] === undefined || segments[i].refreshTranslation) {
        // Now call translate API
        const params = {
          Text: segments[i].transcript,
          SourceLanguageCode: 'auto',
          TargetLanguageCode: targetLanguage,
        };
        const command = new TranslateTextCommand(params);

        logger.debug('Translate API being invoked for:', segments[i].transcript, targetLanguage);

        promises.push(
          translateClient.send(command).then(
            (data) => {
              const n = {};
              logger.debug('Translate API response:', segments[i].transcript, targetLanguage, data.TranslatedText);
              n[segmentLanguageId] = { 
                cacheId: segmentLanguageId, 
                transcript: segments[i].transcript, 
                translated: 
                data.TranslatedText 
              };
              return n;
            },
            (error) => {
              logger.debug('Error from translate:', error);
            },
          ),
        );
      }
    }
    return promises;
  };

  // Translate all segments when the call is completed.
  useEffect(() => {
    if (translateOn && targetLanguage !== '' && item.recordingStatusLabel !== IN_PROGRESS_STATUS) {
      const promises = updateTranslateCache(getSegments());
      Promise.all(promises).then((results) => {
        // prettier-ignore
        if (results.length > 0) {
          setTranslateCache((state) => ({
            ...state,
            ...results.reduce((a, b) => ({ ...a, ...b })),
          }));
          setUpdateFlag((state) => !state);
        }
      });
      setEditedSegment(null);
    }
    // Adding editedSegment to redrive translation of messages
  }, [targetLanguage, agentTranscript, translateOn, item.recordingStatusLabel, editedSegment]);

  // Translate real-time segments when the call is in progress.
  useEffect(async () => {
    const c = getSegments();
    setUpdatedTurnByTurnSegments(c);
    if (translateOn && targetLanguage !== '' && c.length > 0 && item.recordingStatusLabel === IN_PROGRESS_STATUS) {
      const k = c[c.length - 1].segmentId.concat('-', targetLanguage);
      const n = {};
      if (c[c.length - 1].isPartial === false && cacheSeen[k] === undefined) {
        n[k] = { seen: true };
        setCacheSeen((state) => ({
          ...state,
          ...n,
        }));

        // prettier-ignore
        if (translateCache[k] === undefined) {
          // Now call translate API
          const params = {
            Text: c[c.length - 1].transcript,
            SourceLanguageCode: 'auto',
            TargetLanguageCode: targetLanguage,
          };
          const command = new TranslateTextCommand(params);

          logger.debug('Translate API being invoked for:', c[c.length - 1].transcript, targetLanguage);

          try {
            const data = await translateClient.send(command);
            const o = {};
            logger.debug('Translate API response:', c[c.length - 1].transcript, data.TranslatedText);
            o[k] = {
              cacheId: k,
              transcript: c[c.length - 1].transcript,
              translated: data.TranslatedText,
            };
            setTranslateCache((state) => ({
              ...state,
              ...o,
            }));
          } catch (error) {
            logger.debug('Error from translate:', error);
          }
        }
      }
      if (Date.now() - lastUpdated > 500) {
        setUpdateFlag((state) => !state);
        logger.debug('Updating turn by turn with latest cache');
      }
    }
    setLastUpdated(Date.now());
  }, [callTranscriptPerCallId]);

  const getTurnByTurnSegments = (currentTurnByTurnSegments) => {
    const handleSegmentTranscriptUpdate = (updatedSegment) => {
      setUpdatedTurnByTurnSegments(
        currentTurnByTurnSegments.map((segment) => {
          const entireSegment = segment;
          if (entireSegment.segmentId === updatedSegment.segmentId) {
            entireSegment.transcript = updatedSegment.transcript;
            entireSegment.edited = updatedSegment.edited;
            entireSegment.refreshTranslation = updatedSegment.refreshTranslation;
            entireSegment.annotation = updatedSegment.annotation;
            setEditedSegment(entireSegment);
          }
          return entireSegment;
        }),
      );
    };
    const turnSegments = transcriptChannels
      .map((c) => {
        const { segments } = transcriptsForThisCallId[c];
        return segments;
      })
      // sort entries by end time
      .reduce((p, c) => [...p, ...c].sort((a, b) => a.endTime - b.endTime), [])
      .reduce((accumulator, current) => {
        if (
          // prettier-ignore
          !accumulator.length
          || !shouldAppendToPreviousSegment(
            { previous: accumulator[accumulator.length - 1], current },
          )
          // Enable it once it is compatible with translation
          || translateOn
        ) {
          // Get copy of current segment to avoid direct modification
          accumulator.push({ ...current });
        } else {
          appendToPreviousSegment({ previous: accumulator[accumulator.length - 1], current });
        }
        return accumulator;
      }, [])
      .map((c) => {
        const t = c;
        t.agentTranscript = agentTranscript;
        t.targetLanguage = targetLanguage;
        t.translateOn = translateOn;
        // In streaming audio the speaker will just be "Other participant", override this with the
        // name the user chose if needed
        if (t.speaker === DEFAULT_OTHER_SPEAKER_NAME || t.speaker === '') {
          t.speaker = item.callerPhoneNumber || DEFAULT_OTHER_SPEAKER_NAME;
        }

        return t;
      })
      .map((s) => {
        const result = s?.segmentId &&
          s?.createdAt &&
          (s.agentTranscript === undefined || s.agentTranscript || s.channel !== 'AGENT') &&
          s.channel !== 'AGENT_VOICETONE' &&
          s.channel !== 'CALLER_VOICETONE' &&
          (!showAnnotationsOnly || (showAnnotationsOnly && s.annotation)) && (
            <TranscriptSegment
              key={`${s.segmentId}-${s.createdAt}`}
              segment={s}
              translateCache={translateCache}
              enableSentimentAnalysis={enableSentimentAnalysis}
              participantName={item.callerPhoneNumber}
              showAnnotationsOnly={showAnnotationsOnly}
              handleTranscriptSegmentUpdate={handleSegmentTranscriptUpdate}
            />
          );

        // If there was an empty string annotation, it was returning an empty div to the transcript section
        // We should return undefined to make sure that there isn't an odd empty space
        if (!result) {
          return undefined;
        }
        return result;
      });

    // this element is used for scrolling to bottom and to provide padding
    turnSegments.push(<div key="bottom" ref={bottomRef} />);
    return turnSegments;
  };

  useEffect(() => {
    setTurnByTurnSegments(getTurnByTurnSegments(updatedTurnByTurnSegments));
  }, [
    callTranscriptPerCallId,
    item.recordingStatusLabel,
    targetLanguage,
    agentTranscript,
    translateOn,
    updateFlag,
    showAnnotationsOnly,
    updatedTurnByTurnSegments,
  ]);

  useEffect(() => {
    // prettier-ignore
    if (
      item.recordingStatusLabel === IN_PROGRESS_STATUS
      && autoScroll
      && bottomRef.current?.scrollIntoView
    ) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [turnByTurnSegments, autoScroll, item.recordingStatusLabel, targetLanguage, agentTranscript, translateOn]);

  return (
    <div
      style={{
        overflowY: 'auto',
        maxHeight: collapseSentiment ? '34vh' : '68vh',
        paddingLeft: '10px',
        paddingTop: '10px',
        paddingRight: '10px',
      }}
    >
      <ColumnLayout borders="horizontal" columns={1}>
        {turnByTurnSegments}
      </ColumnLayout>
    </div>
  );
};

const getChatWindow = (item, collapseSentiment) => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState(item.totalConversationDurationMillis);
  const [isError, setIsError] = useState(false);
  const messagesEndRef = useRef(null);
  const { segmentId } = item;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getTimeInSeconds = (timestamp) => {
    return timestamp / ONE_THOUSAND_MS;
  };

  const daysToYears = (days) => {
    return days / 365;
  };

  const handleSubmit = async () => {
    setIsError(false);
    if (currentMessage.trim()) {
      // TODO: address a mid-meeting refresh timestamp issue - https://taskei.amazon.dev/tasks/P212273585

      // When there are annotations submitted without any new conversation, item.totalConversationDurationMillis
      // doesn't change between annotation submitions. We need to have the end time of the annotation submitted to be
      // different to account for the sorting of the transcript after the meeting is over. We will use
      // lastMessageTimestamp as the time if there are back to back annotations submitted.
      const startTimeMs =
        item.totalConversationDurationMillis >= lastMessageTimestamp
          ? item.totalConversationDurationMillis
          : lastMessageTimestamp;
      const startTime = getTimeInSeconds(startTimeMs);
      const endTimeMs = startTimeMs + ONE_HUNDRED_MS;
      const endTime = getTimeInSeconds(endTimeMs);
      setLastMessageTimestamp(endTimeMs);
      const formattedTimestamp = getTimestampFromSeconds(endTime);

      const id = `${ANNOTATOR}-${startTime}`;
      const now = Date.now();
      const newMessage = {
        text: currentMessage.trim(),
        timestamp: formattedTimestamp,
        id: now.toString(),
      };
      const ttlDateFromNow = new Date(now);
      ttlDateFromNow.setFullYear(ttlDateFromNow.getFullYear() + daysToYears(MEETING_RECORD_EXPIRATION_IN_DAYS));
      const ttl = Math.floor(ttlDateFromNow.getTime() / ONE_THOUSAND_MS);
      const result = await API.graphql({
        query: addAnnotation,
        variables: {
          input: {
            CallId: item.callId,
            Status: 'TRANSCRIBING',
            SegmentId: id,
            StartTime: startTime,
            EndTime: endTime,
            Transcript: '',
            IsPartial: false,
            Channel: 'CALLER',
            Speaker: ANNOTATOR,
            ExpiresAfter: ttl,
            Owner: item.owner,
            Annotation: newMessage.text,
          },
        },
      });
      if (result.errors) {
        logger.error('Error creating an annotation segment: ', result.errors[0]);
        setCurrentMessage(newMessage.text);
        setIsError(true);
      } else {
        setMessages([...messages, newMessage]);
        setCurrentMessage('');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  return (
    <Container
      fitHeight="true"
      disableContentPaddings
      header={
        <>
          <Header variant="h4">Live Note Taking</Header>
          <Header variant="p">
            This note taking ability will disappear when you refresh the page after the meeting is over. The notes will
            appear in the Meeting Transcript as annotations once leaving the page or refreshing after the meeting has
            ended. <br /> <br /> This feature is currently in development so do not refresh during an in-progress
            meeting otherwise you might lose some of your notes.
          </Header>
        </>
      }
    >
      <div
        style={{
          overflowY: 'auto',
          maxHeight: collapseSentiment ? '34vh' : '68vh',
          paddingLeft: '10px',
          paddingTop: '10px',
          paddingRight: '10px',
        }}
      >
        <ColumnLayout borders="horizontal" columns={1}>
          <Grid key={`${segmentId}`} disableGutters gridDefinition={[{ colspan: 10 }]}>
            <SpaceBetween direction="vertical" size="xxs">
              {messages.map((message) => (
                <SpaceBetween direction="horizontal" size="xxs">
                  <TextContent>
                    <p style={{ color: '#5f6b7a' }}>{message.timestamp}</p>
                  </TextContent>
                  <TextContent>
                    <p
                      style={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%',
                      }}
                    >
                      {message.text}
                    </p>
                  </TextContent>
                </SpaceBetween>
              ))}
            </SpaceBetween>
          </Grid>
        </ColumnLayout>
        <div ref={messagesEndRef} />
      </div>
      <Box
        padding="l"
        style={{
          borderTop: '1px',
        }}
      >
        <Grid gridDefinition={[{ colspan: isError ? 8 : 10 }, { colspan: isError ? 4 : 2 }]}>
          <textarea
            value={currentMessage}
            onKeyUp={handleKeyPress}
            onChange={(e) => setCurrentMessage(e.target.value)}
            placeholder="Type your note here..."
            style={{
              width: '100%',
              minHeight: '100px',
              resize: 'vertical',
              padding: '8px',
              boxSizing: 'border-box',
            }}
          />
          {isError ? (
            <Button variant="primary" onClick={handleSubmit} style={{ width: '100%' }}>
              Try Again
            </Button>
          ) : (
            <Button
              iconName="arrow-right"
              variant="primary"
              onClick={handleSubmit}
              disabled={!currentMessage.trim()}
              style={{ width: '100%' }}
            />
          )}
        </Grid>
      </Box>
    </Container>
  );
};

const getTranscriptContent = ({
  item,
  callTranscriptPerCallId,
  autoScroll,
  translateClient,
  targetLanguage,
  agentTranscript,
  translateOn,
  collapseSentiment,
  enableSentimentAnalysis,
  showAnnotationsOnly,
}) => {
  switch (item.recordingStatusLabel) {
    case DONE_STATUS:
    case IN_PROGRESS_STATUS:
    default:
      return (
        <CallInProgressTranscript
          item={item}
          callTranscriptPerCallId={callTranscriptPerCallId}
          autoScroll={autoScroll}
          translateClient={translateClient}
          targetLanguage={targetLanguage}
          agentTranscript={agentTranscript}
          translateOn={translateOn}
          collapseSentiment={collapseSentiment}
          enableSentimentAnalysis={enableSentimentAnalysis}
          showAnnotationsOnly={showAnnotationsOnly}
        />
      );
  }
};

const CallTranscriptContainer = ({
  setToolsOpen,
  item,
  callTranscriptPerCallId,
  translateClient,
  collapseSentiment,
  enableSentimentAnalysis,
}) => {
  const [autoScroll, setAutoScroll] = useState();
  const [autoScrollDisabled, setAutoScrollDisabled] = useState(item.recordingStatusLabel !== IN_PROGRESS_STATUS);
  const [showDownloadTranscript, setShowDownloadTranscripts] = useState(item.recordingStatusLabel === DONE_STATUS);

  const [showAnnotationsOnly, setShowAnnotationsOnly] = useState(false);
  const [translateOn, setTranslateOn] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState(localStorage.getItem('targetLanguage') || '');
  const [agentTranscript] = useState(true);
  const [showChatWindow, setShowChatWindow] = useState(item.recordingStatusLabel === IN_PROGRESS_STATUS);
  const [firstLoadInProgress, setFirstLoadInProgress] = useState(false);

  const handleLanguageSelect = (event) => {
    setTargetLanguage(event.target.value);
    localStorage.setItem('targetLanguage', event.target.value);
  };

  useEffect(() => {
    setAutoScrollDisabled(item.recordingStatusLabel !== IN_PROGRESS_STATUS);
    setAutoScroll(item.recordingStatusLabel !== IN_PROGRESS_STATUS);
    setShowDownloadTranscripts(item.recordingStatusLabel === DONE_STATUS);
    // We want the Chat window to show during the in progress meeting and once it is done, allowing the annotator to
    // finish taking notes on the meeting, even after it is done. However once the user leaves the page, we will show
    // the QNA bot again.
    if (item.recordingStatusLabel === IN_PROGRESS_STATUS) {
      setFirstLoadInProgress(true);
    } else if (item.recordingStatusLabel === DONE_STATUS) {
      setShowChatWindow(firstLoadInProgress);
      setFirstLoadInProgress(false);
    }
  }, [item.recordingStatusLabel]);

  const languageChoices = () => {
    if (translateOn) {
      return (
        // prettier-ignore
        // eslint-disable-jsx-a11y/control-has-associated-label
        <div>
          <select value={targetLanguage} onChange={handleLanguageSelect}>
            {LANGUAGE_CODES.map(({ value, label }) => <option value={value}>{label}</option>)}
          </select>
        </div>
      );
    }
    return translateOn;
  };

  const downloadTranscript = (option) => {
    if (option.detail.id === 'text') {
      downloadTranscriptAsText(callTranscriptPerCallId, item);
    } else if (option.detail.id === 'excel') {
      downloadTranscriptAsExcel(callTranscriptPerCallId, item);
    }
  };

  return (
    <Grid
      gridDefinition={[
        {
          colspan: {
            default: 12,
            xs: showChatWindow ? 8 : 12,
          },
        },
        {
          colspan: {
            default: 12,
            xs: showChatWindow ? 4 : 0,
          },
        },
      ]}
    >
      <Container
        fitHeight="true"
        disableContentPaddings
        header={
          <Header
            variant="h4"
            info={<InfoLink onFollow={() => setToolsOpen(true)} />}
            actions={
              <SpaceBetween direction="vertical" size="xs">
                <SpaceBetween direction="horizontal" size="xs">
                  <Toggle
                    onChange={({ detail }) => setAutoScroll(detail.checked)}
                    checked={autoScroll}
                    disabled={autoScrollDisabled}
                  />
                  <span>Auto Scroll</span>
                  <Toggle
                    onChange={({ detail }) => setShowAnnotationsOnly(detail.checked)}
                    checked={showAnnotationsOnly}
                  />
                  <span>Annotations Only</span>
                  <Toggle onChange={({ detail }) => setTranslateOn(detail.checked)} checked={translateOn} />
                  <span>Enable Translation</span>
                  {languageChoices()}
                  {showDownloadTranscript && (
                    <SpaceBetween direction="horizontal" size="xs">
                      <ButtonDropdown
                        items={[
                          {
                            text: 'Download as',
                            iconName: 'download',
                            items: [
                              { text: 'Excel', id: 'excel', disabled: false },
                              { text: 'Text', id: 'text' },
                            ],
                          },
                        ]}
                        variant="normal"
                        onItemClick={(option) => downloadTranscript(option)}
                      >
                        <Icon name="download" variant="primary" />
                      </ButtonDropdown>
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              </SpaceBetween>
            }
          >
            Meeting Transcript
          </Header>
        }
      >
        {getTranscriptContent({
          item,
          callTranscriptPerCallId,
          autoScroll,
          translateClient,
          targetLanguage,
          agentTranscript,
          translateOn,
          collapseSentiment,
          enableSentimentAnalysis,
          showAnnotationsOnly,
        })}
      </Container>
      {showChatWindow && getChatWindow(item, collapseSentiment)}
    </Grid>
  );
};

const VoiceToneContainer = ({ item, callTranscriptPerCallId, collapseSentiment, setCollapseSentiment }) => (
  <Container
    fitHeight="true"
    disableContentPaddings={collapseSentiment ? '' : 'true'}
    header={
      <Header
        variant="h4"
        info={
          <Link
            variant="info"
            target="_blank"
            href="https://docs.aws.amazon.com/chime-sdk/latest/dg/call-analytics.html"
          >
            Info
          </Link>
        }
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="inline-icon"
              iconName={collapseSentiment ? 'angle-up' : 'angle-down'}
              onClick={() => setCollapseSentiment(!collapseSentiment)}
            />
          </SpaceBetween>
        }
      >
        Voice Tone Analysis (30sec rolling window)
      </Header>
    }
  >
    {collapseSentiment ? (
      <VoiceToneFluctuationChart item={item} callTranscriptPerCallId={callTranscriptPerCallId} />
    ) : null}
  </Container>
);

const CallStatsContainer = ({ item, callTranscriptPerCallId, collapseSentiment, setCollapseSentiment }) => (
  <>
    <Container
      disableContentPaddings={collapseSentiment ? '' : 'true'}
      header={
        <Header
          variant="h4"
          info={
            <Link
              variant="info"
              target="_blank"
              href="https://docs.aws.amazon.com/transcribe/latest/dg/call-analytics-insights.html#call-analytics-insights-sentiment"
            >
              Info
            </Link>
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="inline-icon"
                iconName={collapseSentiment ? 'angle-up' : 'angle-down'}
                onClick={() => setCollapseSentiment(!collapseSentiment)}
              />
            </SpaceBetween>
          }
        >
          Meeting Sentiment Analysis
        </Header>
      }
    >
      {collapseSentiment ? (
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <SentimentFluctuationChart item={item} callTranscriptPerCallId={callTranscriptPerCallId} />
          <SentimentPerQuarterChart item={item} callTranscriptPerCallId={callTranscriptPerCallId} />
        </Grid>
      ) : null}
    </Container>
    {collapseSentiment ? (
      <Container style={{ display: collapseSentiment ? 'block' : 'none' }}>
        <ColumnLayout columns={4} variant="text-grid">
          <SpaceBetween size="xs">
            <div>
              <Box margin={{ bottom: 'xxxs' }} color="text-label">
                <strong>Caller Avg Sentiment:</strong>
              </Box>
              <div>
                <SentimentIcon sentiment={item.callerSentimentLabel} />
                &nbsp;
                {item.callerAverageSentiment.toFixed(3)}
                <br />
                (min: -5, max: +5)
              </div>
            </div>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <div>
              <Box margin={{ bottom: 'xxxs' }} color="text-label">
                <strong>Caller Sentiment Trend:</strong>
              </Box>
              <div>
                <SentimentTrendIcon trend={item.callerSentimentTrendLabel} />
              </div>
            </div>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <div>
              <Box margin={{ bottom: 'xxxs' }} color="text-label">
                <strong>Agent Avg Sentiment:</strong>
              </Box>
              <div>
                <SentimentIcon sentiment={item.agentSentimentLabel} />
                &nbsp;
                {item.agentAverageSentiment.toFixed(3)}
                <br />
                (min: -5, max: +5)
              </div>
            </div>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <div>
              <Box margin={{ bottom: 'xxxs' }} color="text-label">
                <strong>Agent Sentiment Trend:</strong>
              </Box>
              <div>
                <SentimentTrendIcon trend={item.agentSentimentTrendLabel} />
              </div>
            </div>
          </SpaceBetween>
        </ColumnLayout>
      </Container>
    ) : null}
  </>
);

export const CallPanel = ({ item, callTranscriptPerCallId, setToolsOpen, getCallDetailsFromCallIds }) => {
  const { currentCredentials } = useAppContext();

  const { settings } = useSettingsContext();
  const [collapseSentiment, setCollapseSentiment] = useState(false);

  const enableVoiceTone = settings?.EnableVoiceToneAnalysis === 'true';
  const enableSentimentAnalysis = settings?.IsSentimentAnalysisEnabled === 'true';

  // prettier-ignore
  const customRetryStrategy = new StandardRetryStrategy(
    async () => MAXIMUM_ATTEMPTS,
    {
      delayDecider:
        (_, attempts) => Math.floor(
          Math.min(ONE_THOUSAND_MS, 2 ** attempts * 10),
        ),
    },
  );

  let translateClient = new TranslateClient({
    region: awsExports.aws_project_region,
    credentials: currentCredentials,
    maxAttempts: MAXIMUM_ATTEMPTS,
    retryStrategy: customRetryStrategy,
  });

  /* Get a client with refreshed credentials. Credentials can go stale when user is logged in
     for an extended period.
   */
  useEffect(() => {
    logger.debug('Translate client with refreshed credentials');
    translateClient = new TranslateClient({
      region: awsExports.aws_project_region,
      credentials: currentCredentials,
      maxAttempts: MAXIMUM_ATTEMPTS,
      retryStrategy: customRetryStrategy,
    });
  }, [currentCredentials]);

  return (
    <SpaceBetween size="s">
      <CallAttributes item={item} setToolsOpen={setToolsOpen} getCallDetailsFromCallIds={getCallDetailsFromCallIds} />
      <CallTranscriptContainer
        item={item}
        setToolsOpen={setToolsOpen}
        callTranscriptPerCallId={callTranscriptPerCallId}
        translateClient={translateClient}
        collapseSentiment={collapseSentiment}
        enableSentimentAnalysis={enableSentimentAnalysis}
      />
      <CallSummary item={item} />
      {(enableSentimentAnalysis || enableVoiceTone) && (
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xs: enableVoiceTone && enableSentimentAnalysis ? 8 : 12 } },
            { colspan: { default: 12, xs: enableVoiceTone && enableSentimentAnalysis ? 4 : 0 } },
          ]}
        >
          {enableSentimentAnalysis && (
            <CallStatsContainer
              item={item}
              callTranscriptPerCallId={callTranscriptPerCallId}
              collapseSentiment={collapseSentiment}
              setCollapseSentiment={setCollapseSentiment}
            />
          )}
          {enableVoiceTone && (
            <VoiceToneContainer
              item={item}
              callTranscriptPerCallId={callTranscriptPerCallId}
              collapseSentiment={collapseSentiment}
              setCollapseSentiment={setCollapseSentiment}
            />
          )}
        </Grid>
      )}
    </SpaceBetween>
  );
};

export default CallPanel;
